import React, { useState, useEffect, useCallback } from 'react';
import { Envelope, Warning, EnvelopeSimple, Info, WarningCircle } from '@phosphor-icons/react';
import { useOneButtonStore, COMMON_CONSTANTS } from '../../../../store/oneButtonStore';
import opportunityInfoServices from '../../../../services/oneButtonServices/opportunityInfoService';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import DashboardModal from './DashboardModal';
import { OBDSelect } from './form-elements';
import './oneButton-css/CreditCheckModal.css';
import { OBD_FINANCE_PARTNER_PROGRESS_Z, OBD_FINANCE_PARTNER_SYNC_Z } from './oneButtonOverlayZ';
import './oneButton-css/OneButtonFormControls.css';

export const CreditCheckModal: React.FC = () => {
    const open = useOneButtonStore((s) => s.creditCheckModalOpen);
    const setOpen = useOneButtonStore((s) => s.setCreditCheckModalOpen);
    const partnerNameFromStore = useOneButtonStore((s) => s.creditCheckPartnerName);
    const theme = useOneButtonStore((s) => s.theme);
    const opportunityId = useOneButtonStore((s) => s.opportunityID);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);

    const [loading, setLoading] = useState(false);
    const [contacts, setContacts] = useState<any[]>([]);
    const [selectedContactId, setSelectedContactId] = useState<string>('');
    const [mode, setMode] = useState<'InPerson' | 'Email'>('InPerson');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const [isIGS, setIsIGS] = useState(false);
    const [isLightReach, setIsLightReach] = useState(false);
    const [isCheckCreditTriggered, setIsCheckCreditTriggered] = useState(false);
    const [showEligibilityWarning, setShowEligibilityWarning] = useState(false);
    const [creditContent, setCreditContent] = useState('');
    const [signingURL, setSigningURL] = useState('');
    const [iframeVisible, setIframeVisible] = useState(false);
    const [busyText, setBusyText] = useState('Loading');
    const disableElectronicConsent = false;

    const fetchContacts = useCallback(async () => {
        if (!opportunityId) {
            console.warn("CreditCheckModal: No opportunityId found in store");
            return;
        }
        
        setBusyText('Loading');
        setLoading(true);
        try {
            // Pre-resolve basic info from store even if API fails
            // Determine if it's IGS or Lightreach based on store-passed partner name OR detected products
            let isIgsPartner = false;
            let isLightreachPartner = false;

            if (partnerNameFromStore) {
                const up = partnerNameFromStore.toUpperCase();
                isIgsPartner = up.includes('IGS');
                isLightreachPartner = up.includes('LIGHTREACH');
            } else {
                const products = opportunityDetails?.OpportunityProduct || opportunityDetails?.opportunityProduct || [];
                const foundIgs = products.some((p: any) => {
                    const pn = (p.Finance_Partner_Display_Name || p.Finance_Partner || '').toUpperCase();
                    return pn.includes('IGS');
                });
                const foundLr = products.some((p: any) => {
                    const pn = (p.Finance_Partner_Display_Name || p.Finance_Partner || '').toUpperCase();
                    return pn.includes('LIGHTREACH');
                });
                isIgsPartner = foundIgs;
                isLightreachPartner = foundLr;
            }

            setIsIGS(isIgsPartner);
            setIsLightReach(isLightreachPartner);
            setMode(isIgsPartner ? 'Email' : 'InPerson');

            const primaryCustomer = (opportunityDetails?.Customers || []).find((c: any) => c.IsPrimaryContact);
            if (primaryCustomer) {
                setEmail(primaryCustomer.Email || '');
            }

            // Now try to fetch contact list for the dropdown
            const res = await opportunityInfoServices.displayContacts(opportunityId);
            const contactsList = Array.isArray(res) ? res : [];
            const processedContacts = contactsList.map((c: any, index: number) => ({
                id: String(index), 
                contactId: c.contactId,
                name: c.name,
                email: c.email
            }));
            setContacts(processedContacts);

            if (processedContacts.length > 0) {
                let foundId = '';

                if (primaryCustomer) {
                    const fullName = `${primaryCustomer.FirstName} ${primaryCustomer.LastName}`.trim().toLowerCase();
                    const custContactId = String(primaryCustomer.Finance_Partner_Sync_ContactId || '').trim();
                    
                    const found = processedContacts.find(c => {
                        const matchId = c.contactId && String(c.contactId).trim() === custContactId;
                        const matchName = c.name && c.name.toLowerCase().trim() === fullName;
                        return matchId || matchName;
                    });
                    
                    if (found) {
                        foundId = found.id;
                    }
                }

                // Fallback: If no match but only one contact, auto-select it
                if (!foundId && processedContacts.length === 1) {
                    foundId = processedContacts[0].id;
                }

                if (foundId) {
                    setSelectedContactId(foundId);
                    // Update email if it was missing from initial map
                    const contact = processedContacts.find(c => c.id === foundId);
                    if (contact && !email) {
                        const cust = (opportunityDetails?.Customers || []).find(
                            (c: any) => String(c.Finance_Partner_Sync_ContactId) === String(contact.contactId)
                        );
                        setEmail(cust?.Email || contact.email || email);
                    }
                }
            }

            const customers = (opportunityDetails?.Customers || []);
            const hasMissingSyncId = customers.some((c: any) => !c.Finance_Partner_Sync_ContactId);
            setShowEligibilityWarning(hasMissingSyncId);

        } catch (err) {
            console.error("CreditCheckModal: Failed to fetch contacts list, but continuing with store data", err);
        } finally {
            setLoading(false);
        }
    }, [opportunityId, opportunityDetails]);
    
    const refreshStatuses = useCallback(async (includeOppDetail = false) => {
        const currentOppId = useOneButtonStore.getState().opportunityID;
        if (!currentOppId) return;
        try {
            // Refresh sync status list
            const syncRes = await quoteServices.getSyncStatusList(String(currentOppId));
            const syncList = syncRes?.FinancePartnerSyncStatuses || (Array.isArray(syncRes) ? syncRes : []);
            if (Array.isArray(syncList)) {
                useOneButtonStore.getState().setFinancePartnerRawSyncStatuses(syncList);
            }
            
            // Refresh general opportunity details only on successful credit trigger
            if (includeOppDetail) {
                const state = useOneButtonStore.getState();
                const salesforceId = state.opportunityDetails?.SalesForce_Id || state.opportunityDetails?.Salesforce_Id || currentOppId;
                const oppDetail = await opportunityInfoServices.getOpportunityDetail(String(salesforceId));
                if (oppDetail) {
                    useOneButtonStore.getState().setOpportunityDetails(oppDetail);
                }
            }
        } catch (err) {
            console.error("CreditCheckModal: Failed to refresh dashboard status", err);
        }
    }, []);

    /**
     * Trinity `credit-status-renderer` → `afterClose` → `refreshData` → `contract-list.retrieveContract(false)` so
     * Lightreach / other grids reload `CreditStatus` from `RetrieveContract`.
     */
    const bumpAgreementsContractReload = useCallback(() => {
        useOneButtonStore.getState().setAgreementsRefreshTrigger(Date.now());
    }, []);

    const handleClose = useCallback(async () => {
        await refreshStatuses(isCheckCreditTriggered);
        bumpAgreementsContractReload();
        setOpen(false);
    }, [isCheckCreditTriggered, refreshStatuses, bumpAgreementsContractReload, setOpen]);

    useEffect(() => {
        if (open) {
            setMessage('');
            setError(null);
            setCreditContent('');
            setIsCheckCreditTriggered(false);
            setSigningURL('');
            setIframeVisible(false);
        }
    }, [open]);

    useEffect(() => {
        if (open) {
            fetchContacts();
        }
    }, [open, fetchContacts]);

    const handleContactChange = (val: string) => {
        setSelectedContactId(val);
        const contact = contacts.find(c => c.id === val);
        if (contact) {
            const primaryCustomer = (opportunityDetails?.Customers || []).find(
                (c: any) => String(c.Finance_Partner_Sync_ContactId) === String(contact.contactId)
            );
            if (primaryCustomer) setEmail(primaryCustomer.Email || '');
            else setEmail(contact.email || '');
        }
    };

    const handleCheckCredit = async () => {
        if (!opportunityId) return;
        if (selectedContactId === '' || !mode) {
            setError("Please ensure Credit Contact is filled before clicking Check Credit");
            return;
        }

        setBusyText('Loading');
        setLoading(true);
        setError(null);
        setCreditContent('');        

        try {
            // Resolve Lead ID exactly as Angular logic
            const leadId = opportunityDetails?.Finance_Partner_Sync_LeadID || 
                           opportunityDetails?.OpportunityDetails?.Finance_Partner_Sync_LeadID ||
                           opportunityDetails?.Finance_Partner_Sync_CO_LeadID;
            
            if (isIGS) {
                const res = disableElectronicConsent 
                    ? await opportunityInfoServices.getCreditEnquiryInvitation(leadId)
                    : await opportunityInfoServices.ElectronicConsent(leadId);
                    
                if (res.IsSuccessful) {
                    setIsCheckCreditTriggered(true);
                    setMessage("The credit check request has been sent to IGS. Instruct the customer to check their email for the message from IGS to begin the process.");
                } else {
                    let errMsg = "Credit check request failed, please try again";
                    try {
                        if (res.Message && typeof JSON.parse(res.Message) === 'object') {
                            errMsg = JSON.parse(res.Message)?.message + " please try again";
                        }
                    } catch (e) {
                        errMsg = res.Message || errMsg;
                    }
                    setError(errMsg);
                }
            } else {
                // Handling for Lightreach, Sunlight, and other finance partners
                const contactData = contacts.find(c => c.id === selectedContactId);
                const payload = {
                    opportunityId: opportunityId,
                    finance_Partner_Sync_contactId: contactData?.contactId,
                    finance_Partner_Sync_Id: leadId,
                    isEmail: mode.toLowerCase() === 'email'
                };
                
                const res = await opportunityInfoServices.checkCreditFinancePartner(payload);
                
                if (res.IsSuccessful) {
                    setIsCheckCreditTriggered(true);
                    if (isLightReach) {
                        setSigningURL(res.Data);
                        setIframeVisible(true);
                        setOpen(false);
                    } else if (mode === 'InPerson') {
                        setSigningURL(res.Data);
                        setIframeVisible(true);
                    } else {
                        // For non-IGS Email: Close modal immediately and refresh since no iframe
                        await refreshStatuses(true);
                        bumpAgreementsContractReload();
                        setOpen(false);
                    }
                } else {
                    setError("Error while requesting Credit Check.");
                }
            }
        } catch (err: any) {
            console.error("CreditCheckModal: Submission Error", err);
            setError(err.message || "An unexpected error occurred.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
        <DashboardModal
            open={open}
            onClose={handleClose}
            title="Check Credit"
            width={600}
            maskClosable={false}
            zIndex={OBD_FINANCE_PARTNER_SYNC_Z}
            theme={theme}
            className={`obd-sync-modal obd-credit-check-modal obd-theme-${theme}`}
            rootClassName={`obd-sync-modal-root obd-credit-check-modal-root obd-theme-${theme}`}
            centered
            destroyOnClose
            footer={null}
            loading={loading}
            loadingMessage={busyText}
        >
            <div className="obd-sync-modal__inner" style={{ position: 'relative' }}>
                <div className="obd-sync-modal__header">
                    <h3 className="obd-sync-modal__title">Check Credit</h3>
                </div>

                <div className="obd-sync-modal__content-body obd-credit-modal__body">
                    {showEligibilityWarning && mode !== 'Email' && (
                        <div className="obd-credit-modal__eligibility-box">
                            <div className="obd-notice-content">
                                <Info size={22} weight="fill" className="obd-notice-icon" />
                                <div className="obd-notice-text">
                                    <p>Contacts must be added to contract and synced to finance partner for credit check eligibility</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {!isCheckCreditTriggered || (mode === 'InPerson' && !isIGS) || creditContent || isLightReach ? (
                        <div className="obd-credit-modal__form">
                            <div className="obd-credit-modal__row">
                                <div className="obd-credit-modal__field">
                                    <label>Credit Contact</label>
                                    <OBDSelect
                                        value={selectedContactId} 
                                        onChange={handleContactChange}
                                        disabled={loading || isIGS || !!creditContent}
                                        placeholder="Choose..."
                                        className="obd-select-field"
                                        style={{ width: '100%' }}
                                        options={contacts.map((c) => ({ value: c.id, label: c.name }))}
                                    />
                                </div>
                                <div className="obd-credit-modal__field">
                                    <label>Mode</label>
                                    <OBDSelect
                                        value={mode} 
                                        onChange={(v) => setMode(v as any)}
                                        disabled={loading || isIGS || !!creditContent}
                                        className="obd-select-field"
                                        style={{ width: '100%' }}
                                        options={[
                                            { value: 'InPerson', label: 'In-Person' },
                                            { value: 'Email', label: 'Email' },
                                        ]}
                                    />
                                </div>
                            </div>

                            {mode === 'Email' && !creditContent && (
                                <div className="obd-credit-modal__notice-box">
                                    <div className="obd-notice-content">
                                        <WarningCircle size={22} weight="fill" className="obd-notice-icon" />
                                        <div className="obd-notice-text">
                                            <p>Before proceeding, please ensure that the selected customer’s contact information is correct. Once the Credit Check is completed, any updates to the customer’s contact information will need to be made by the Finance Partner.</p>
                                        </div>
                                    </div>
                                    <div className="obd-credit-modal__field obd-credit-modal__email-field">
                                        <label>Recipient Email Address</label>
                                        <div className="obd-input-group">
                                            <Envelope size={18} className="obd-input-group__icon" />
                                            <input 
                                                type="email" 
                                                value={email} 
                                                readOnly={true}
                                                className="obd-credit-modal__input"
                                                disabled={loading}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {creditContent && (
                                <div className="obd-credit-modal__success-msg">
                                    {creditContent}
                                </div>
                            )}

                            {error && (
                                <div className="obd-credit-modal__error-message">
                                    <span>{error}</span>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="obd-credit-modal__success-state">
                            <div className="obd-credit-modal__envelop-wrapper">
                                <EnvelopeSimple size={72} weight="fill" className="obd-envelop-svg-animated" />
                            </div>
                            <div className="obd-credit-modal__message-p">
                                {message}
                            </div>
                        </div>
                    )}
                </div>

                <div className="obd-sync-modal__footer">
                    <div className="obd-sync-modal__footer-btns" style={{ width: '100%', justifyContent: 'center' }}>
                        <button 
                            type="button" 
                            className="obd-sync-modal__btn obd-sync-modal__btn--secondary" 
                            onClick={handleClose}
                        >
                            {isCheckCreditTriggered ? 'Close' : 'Cancel'}
                        </button>
                        {!isCheckCreditTriggered && (
                            <button 
                                type="button" 
                                className="obd-sync-modal__btn obd-sync-modal__btn--primary" 
                                onClick={handleCheckCredit}
                                disabled={loading || !selectedContactId}
                            >
                                Check Credit
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </DashboardModal>

        {iframeVisible && (
             <DashboardModal
                open={iframeVisible}
                onClose={async () => {
                    setIframeVisible(false);
                    await refreshStatuses(true);
                    bumpAgreementsContractReload();
                    setOpen(false);
                }}
                footer={null}
                width="90%"
                maskClosable={false}
                centered
                destroyOnClose
                zIndex={OBD_FINANCE_PARTNER_PROGRESS_Z}
                title="Credit Check Form"
                theme={theme}
                className={`obd-credit-check-form-modal obd-theme-${theme}`}
                rootClassName={`obd-form-controls-modal-root obd-credit-check-form-modal-root obd-theme-${theme}`}
            >
                <div style={{ height: '80vh', position: 'relative' }}>
                    <iframe 
                        src={signingURL} 
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        title="Credit Check"
                    />
                </div>
            </DashboardModal>
        )}
        </>
    );
};

export default CreditCheckModal;
